import React, { useState, useEffect } from 'react';
import { Bell, Phone, Mail, MapPin, Trash2, Check, Clock, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-800' },
  contacted: { label: 'Contacted', color: 'bg-amber-100 text-amber-800' },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-800' },
};

const AdminHoneyRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [editingNotes, setEditingNotes] = useState(null);
  const [notesText, setNotesText] = useState('');
  const token = localStorage.getItem('token');

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/honey-notify-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(res.data.requests || []);
    } catch {
      toast.error('Failed to load honey requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); }, []);

  const updateStatus = async (id, status, notes) => {
    try {
      const body = { status };
      if (notes !== undefined) body.admin_notes = notes;
      await axios.put(`${API}/api/admin/honey-notify-requests/${id}/status`, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Request marked as ${status}`);
      fetchRequests();
      setEditingNotes(null);
    } catch { toast.error('Failed to update'); }
  };

  const deleteRequest = async (id) => {
    if (!window.confirm('Delete this request?')) return;
    try {
      await axios.delete(`${API}/api/admin/honey-notify-requests/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Request deleted');
      fetchRequests();
    } catch { toast.error('Failed to delete'); }
  };

  const filtered = statusFilter
    ? requests.filter(r => r.status === statusFilter)
    : requests;

  const counts = {
    all: requests.length,
    new: requests.filter(r => r.status === 'new').length,
    contacted: requests.filter(r => r.status === 'contacted').length,
    resolved: requests.filter(r => r.status === 'resolved').length,
  };

  return (
    <div className="max-w-7xl" data-testid="admin-honey-requests-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
          <Bell className="h-6 w-6 text-amber-600" />
          Honey Notify Requests
        </h1>
        <p className="text-sm text-stone-500 mt-1">People looking for local honey suppliers in their area. Reach out and find beekeepers near them!</p>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="honey-request-filters">
        {[
          { key: '', label: 'All', count: counts.all },
          { key: 'new', label: 'New', count: counts.new },
          { key: 'contacted', label: 'Contacted', count: counts.contacted },
          { key: 'resolved', label: 'Resolved', count: counts.resolved },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setStatusFilter(tab.key)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              statusFilter === tab.key
                ? 'bg-amber-600 text-white shadow-sm'
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
            data-testid={`filter-${tab.key || 'all'}`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className={`ml-1.5 text-xs ${statusFilter === tab.key ? 'text-amber-200' : 'text-stone-400'}`}>
                ({tab.count})
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-stone-400">Loading requests...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-stone-200">
          <Bell className="h-12 w-12 text-stone-200 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-stone-700">No requests found</h3>
          <p className="text-sm text-stone-500 mt-1">
            {statusFilter ? `No ${statusFilter} requests.` : 'Honey notification requests will appear here when visitors submit them on Find Local Honey.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(req => {
            const cfg = STATUS_STYLES[req.status] || STATUS_STYLES.new;
            return (
              <div key={req.id} className="bg-white rounded-xl border border-stone-200 p-4 hover:border-stone-300 transition-colors" data-testid={`request-${req.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      {req.name && <span className="font-semibold text-stone-900">{req.name}</span>}
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
                        {cfg.label}
                      </span>
                      <span className="text-xs text-stone-400">{req.created_at ? new Date(req.created_at).toLocaleDateString() : ''}</span>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm">
                      {req.email && (
                        <a href={`mailto:${req.email}`} className="flex items-center gap-1.5 text-amber-700 hover:text-amber-900">
                          <Mail className="h-3.5 w-3.5" /> {req.email}
                        </a>
                      )}
                      {req.mobile && (
                        <a href={`tel:${req.mobile}`} className="flex items-center gap-1.5 text-blue-700 hover:text-blue-900">
                          <Phone className="h-3.5 w-3.5" /> {req.mobile}
                        </a>
                      )}
                      {req.location && (
                        <span className="flex items-center gap-1.5 text-stone-600">
                          <MapPin className="h-3.5 w-3.5 text-green-600" /> {req.location}
                        </span>
                      )}
                    </div>
                    {/* Admin Notes */}
                    {editingNotes === req.id ? (
                      <div className="mt-3 space-y-2">
                        <Textarea
                          value={notesText}
                          onChange={(e) => setNotesText(e.target.value)}
                          placeholder="Add notes about this request..."
                          className="text-sm min-h-[60px]"
                          data-testid={`notes-textarea-${req.id}`}
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => updateStatus(req.id, req.status, notesText)} className="bg-amber-600 hover:bg-amber-700 text-white text-xs h-7" data-testid={`save-notes-${req.id}`}>Save Notes</Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingNotes(null)} className="text-xs h-7">Cancel</Button>
                        </div>
                      </div>
                    ) : req.admin_notes ? (
                      <div className="mt-2 bg-stone-50 rounded-lg p-2 text-xs text-stone-600 cursor-pointer hover:bg-stone-100" onClick={() => { setEditingNotes(req.id); setNotesText(req.admin_notes); }}>
                        <MessageSquare className="h-3 w-3 inline mr-1 text-stone-400" /> {req.admin_notes}
                      </div>
                    ) : null}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!req.admin_notes && editingNotes !== req.id && (
                      <Button size="sm" variant="ghost" onClick={() => { setEditingNotes(req.id); setNotesText(''); }} className="h-8 w-8 p-0 text-stone-400 hover:text-stone-600" title="Add Notes" data-testid={`add-notes-${req.id}`}>
                        <MessageSquare className="h-4 w-4" />
                      </Button>
                    )}
                    {req.status !== 'contacted' && (
                      <Button size="sm" variant="ghost" onClick={() => updateStatus(req.id, 'contacted')} className="h-8 w-8 p-0 text-amber-600 hover:bg-amber-50" title="Mark Contacted" data-testid={`contact-${req.id}`}>
                        <Phone className="h-4 w-4" />
                      </Button>
                    )}
                    {req.status !== 'resolved' && (
                      <Button size="sm" variant="ghost" onClick={() => updateStatus(req.id, 'resolved')} className="h-8 w-8 p-0 text-green-600 hover:bg-green-50" title="Mark Resolved" data-testid={`resolve-${req.id}`}>
                        <Check className="h-4 w-4" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => deleteRequest(req.id)} className="h-8 w-8 p-0 text-stone-400 hover:bg-red-50 hover:text-red-600" title="Delete" data-testid={`delete-${req.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminHoneyRequests;
